import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Transition from 'react-transition-group/Transition'
import { cx } from '@emotion/css'
import { Chip, colors } from '@offerzen/pinata-shame'
import MenuIcon from '@offerzen/zenicon/MenuIcon'
import BlocksIcon from '@offerzen/zenicon/BlocksIcon'
import UserIcon from '@offerzen/zenicon/UserIcon'
import ConversationIcon from '@offerzen/zenicon/ConversationIcon'
import CalendarIcon from '@offerzen/zenicon/CalendarIcon'
import WarningCircleIcon from '@offerzen/zenicon/WarningCircleIcon'
import GearIcon from '@offerzen/zenicon/GearIcon'
import BriefcaseIcon from '@offerzen/zenicon/BriefcaseIcon'

import { getCurrentCandidate } from '@/core/selectors/candidate'
import { getIsLinkActive } from '@/core/helpers/navigationHelpers'

import AccessibleIcon from '../AccessibleIcon'

import MobileLink from './MobileLink'
import styles, { wrapperTransition, navTransition } from './styles'

const isArchivedIRs = ({ location }) => location.search.includes('filter=archived')

/**
 * MobileNavMenu component renders a mobile navigation menu for candidates.
 *
 * @param {Object} props
 * @param {string} props.candidateState - The state of the candidate (e.g., 'live').
 * @param {number} props.unreadInterviewRequestCount
 * @param {string} props.emailPreferencesAccessTokenKey
 * @param {boolean} props.disabledCandidate
 * @param {boolean} props.candidateAvailabilitySet
 * @param {Function} props.signOut
 *
 * @returns {JSX.Element} The rendered MobileNavMenu component.
 */
const MobileNavMenu = ({
  candidateState,
  unreadInterviewRequestCount,
  emailPreferencesAccessTokenKey,
  disabledCandidate,
  candidateAvailabilitySet,
  signOut,
}) => {
  const { id: candidateId, slug: candidateSlug } = useSelector(getCurrentCandidate)
  const [open, setOpen] = useState(false)

  const toggleNavbar = () => setOpen(!open)

  return (
    <>
      <div className={styles.menu} onClick={toggleNavbar}>
        <AccessibleIcon icon={MenuIcon} size={24} color={colors.white} />
      </div>

      <Transition in={open} timeout={{ enter: 0, exit: 300 }} mountOnEnter unmountOnExit>
        {status => (
          <div className={cx(styles.wrapper, wrapperTransition(status))}>
            <div className={styles.overlay} onClick={toggleNavbar} />

            <ul className={cx(styles.nav, navTransition(status))}>
              <li className={styles.navItem}>
                <MobileLink
                  exact
                  to={`/candidate/${candidateId}/dashboard`}
                  isActive={getIsLinkActive({ candidateId, candidateSlug, page: 'dashboard' })}
                  toggleNavbar={toggleNavbar}>
                  <AccessibleIcon className={styles.icon} icon={BlocksIcon} size={18} />
                  <span>Dashboard</span>
                </MobileLink>
              </li>

              <li className={styles.navItem}>
                <MobileLink
                  exact
                  to={`/candidate/${candidateId}`}
                  isActive={getIsLinkActive({ candidateId, candidateSlug })}
                  toggleNavbar={toggleNavbar}>
                  <AccessibleIcon className={styles.icon} icon={UserIcon} size={18} />
                  <span>Profile</span>

                  <Chip
                    theme={candidateState === 'live' ? 'success' : 'error'}
                    style="dark"
                    label={candidateState === 'live' ? 'Live' : 'Hidden'}
                    className={styles.chip}
                  />
                </MobileLink>
              </li>

              <li className={styles.navItem}>
                <MobileLink
                  exact
                  to={`/candidate/${candidateId}/jobs`}
                  toggleNavbar={toggleNavbar}
                  isActive={(match, location) => {
                    const isLegacyJobsPath = getIsLinkActive({ candidateId, candidateSlug, page: 'jobs' })(match, location)
                    // We need to check multiple paths here because the applications URL structure is not nested under jobs
                    // (e.g. `/candidate/ID/jobs/applications`). Instead, it's a sibling path (`/candidate/ID/applications`).
                    const isLegacyApplicationPath = location.pathname === `/candidate/${candidateSlug}/applications`
                    const isApplicationPath = location.pathname === `/candidate/${candidateId}/applications`

                    return isLegacyJobsPath || isLegacyApplicationPath || isApplicationPath
                  }}>
                  <AccessibleIcon className={styles.icon} icon={BriefcaseIcon} size={18} />
                  <span>Jobs</span>
                </MobileLink>
              </li>

              <If condition={!disabledCandidate}>
                <li className={styles.navItem}>
                  <MobileLink
                    to={`/candidate/${candidateId}/interview_requests?filter=all`}
                    isActive={getIsLinkActive({ candidateId, candidateSlug, page: 'interview_requests' })}
                    toggleNavbar={toggleNavbar}
                    isMenu>
                    <AccessibleIcon className={styles.icon} icon={ConversationIcon} size={18} />
                    <span>Interviews</span>

                    <If condition={unreadInterviewRequestCount > 0}>
                      <Chip theme="legacy" style="dark" label={unreadInterviewRequestCount} className={styles.chip} />
                    </If>
                  </MobileLink>

                  <ul className={styles.subNav}>
                    <li>
                      <MobileLink
                        to={`/candidate/${candidateId}/interview_requests?filter=all`}
                        isActive={(match, location) => {
                          const isInterviewRequestPath = getIsLinkActive({ candidateId, candidateSlug, page: 'interview_requests' })(
                            match,
                            location,
                          )
                          return isInterviewRequestPath && !isArchivedIRs({ location })
                        }}
                        toggleNavbar={toggleNavbar}
                        isSubLink>
                        <span>Open</span>
                      </MobileLink>
                    </li>
                    <li>
                      <MobileLink
                        to={`/candidate/${candidateId}/interview_requests?filter=archived`}
                        isActive={(match, location) => {
                          const isInterviewRequestPath = getIsLinkActive({ candidateId, candidateSlug, page: 'interview_requests' })(
                            match,
                            location,
                          )
                          return isInterviewRequestPath && isArchivedIRs({ location })
                        }}
                        toggleNavbar={toggleNavbar}
                        isSubLink>
                        <span>Archived</span>
                      </MobileLink>
                    </li>
                  </ul>
                </li>
              </If>

              <If condition={['ready_to_go_live', 'live', 'post_live'].includes(candidateState)}>
                <li className={styles.navItem}>
                  <a href={`/candidate/${candidateId}/calendar`} className={styles.link}>
                    <AccessibleIcon className={styles.icon} icon={CalendarIcon} size={18} />
                    <span>Calendar</span>

                    <If condition={!candidateAvailabilitySet}>
                      <div className={styles.alertIcon}>
                        <AccessibleIcon className={styles.icon} icon={WarningCircleIcon} size={14} color={colors.mandy} />
                      </div>
                    </If>
                  </a>
                </li>
              </If>

              <li className={styles.navItem}>
                <a href={`/candidate/${candidateId}/account/details`} className={styles.link}>
                  <AccessibleIcon className={styles.icon} icon={GearIcon} size={18} />
                  <span>Account</span>
                </a>

                <ul className={styles.subNav}>
                  <li>
                    <a href={`/candidate/${candidateId}/account/details`} className={styles.subLink}>
                      <span>Your details</span>
                    </a>
                  </li>
                  <li>
                    <a href={`/candidate/${candidateId}/account/manage_data`} className={styles.subLink}>
                      <span>Manage your data</span>
                    </a>
                  </li>
                  <li>
                    <a href={`/email_preferences/${emailPreferencesAccessTokenKey}`} className={styles.subLink}>
                      <span>Email preferences</span>
                    </a>
                  </li>
                  <li>
                    <a href="/privacy-centre" className={styles.subLink} target="_blank">
                      <span>Privacy Centre</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://docs.offerzen.com/en/collections/111151-candidate-guides"
                      target="_blank"
                      className={styles.subLink}
                      rel="noreferrer">
                      <span>Help</span>
                    </a>
                  </li>
                  <li>
                    <a rel="nofollow" tabIndex="0" onClick={signOut} className={styles.subLink}>
                      <span>Log out</span>
                    </a>
                  </li>
                </ul>
              </li>

              <li>
                <a href={`/candidate/${candidateId}/referrals?s=sidebar_nav`} className={styles.linkButton}>
                  <span>Invite Friends</span>
                </a>
              </li>
            </ul>
          </div>
        )}
      </Transition>
    </>
  )
}

export default MobileNavMenu
